// @greenlight-spec: 28-it-dashboard-ui.md
// @greenlight-spec: 46-scaling.md#two-planes-one-request-path
// @greenlight-spec: 19-design-system.md
import * as React from 'react';
import { Info } from 'lucide-react';

import { relativeTime } from '@/components/greenlight/relativeTime';
import { SectionCard } from '@/components/greenlight/SectionCard';
import { StatusBadge } from '@/components/greenlight/StatusBadge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { humanCrashReason } from '@/components/health/CrashesSection';
import { seriesColors } from '@/components/health/seriesColors';
import { SeriesDot } from '@/components/health/SeriesDot';
import {
  APP_CAPACITY_CRITICAL_UTILIZATION,
  APP_CAPACITY_WARN_UTILIZATION,
  type HealthSeriesServiceKey,
  type HealthSnapshot,
  type PlatformService,
  type PlatformServiceKey,
  type PlatformServiceReplica,
} from '@greenlight/shared';

/**
 * The two control-plane Deployments as this page names them: the component,
 * with what it carries on the row. Deployment and tier names stay in the spec,
 * the chart, and the fleet monitor.
 */
export const CONTROL_PLANE_TIERS = [
  {
    key: 'serve',
    label: 'Primary server',
    detail:
      'Serves the dashboard, the REST and MCP APIs, and sign-in for every deployed app. While it is down, people cannot reach their apps.',
  },
  {
    key: 'worker',
    label: 'Async worker',
    detail:
      'Runs app deployments, pipeline steps, scheduled jobs and this health check. While it is down, work queues up instead of failing.',
  },
] as const satisfies ReadonlyArray<{ key: 'serve' | 'worker'; label: string; detail: string }>;

/**
 * The services Greenlight runs beside those two, in the order the card lists
 * them, with the tone the page gives each when nothing of it is ready: judged
 * by what breaks, so only ingress, which every request passes through, is red.
 */
export const PLATFORM_SERVICES = [
  {
    key: 'ingress',
    label: 'Ingress',
    detail:
      'Routes every request to the dashboard and to each app, checking sign-in on the way. While it is down, nobody can reach the dashboard or any app.',
    downTone: 'red',
    possessive: 'its',
  },
  {
    key: 'certificates',
    label: 'Certificates',
    detail:
      'Renews the certificates the dashboard and every app are served with. While it is down nothing fails at once, but certificates stop renewing and eventually expire.',
    downTone: 'amber',
    possessive: 'their',
  },
  {
    key: 'renderer',
    label: 'Screenshot renderer',
    detail:
      'Captures the app images the dashboard shows. While it is down, new images wait; apps keep running.',
    downTone: 'amber',
    possessive: 'its',
  },
] as const satisfies ReadonlyArray<{
  key: PlatformServiceKey;
  label: string;
  detail: string;
  downTone: 'red' | 'amber';
  possessive: 'its' | 'their';
}>;

const MIB = 1024 * 1024;

function LabelWithHelp({
  label,
  detail,
}: {
  readonly label: string;
  readonly detail: string;
}): React.ReactElement {
  return (
    <span className="inline-flex items-center gap-1.5">
      {label}
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            aria-label={`What ${label} does`}
            className="rounded-full text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
          >
            <Info className="size-3.5" aria-hidden="true" />
          </button>
        </TooltipTrigger>
        <TooltipContent className="max-w-[260px] text-left leading-relaxed">
          {detail}
        </TooltipContent>
      </Tooltip>
    </span>
  );
}

/** Nothing ready takes the service's own tone; a shortfall is amber for every service. */
function readyVariant(
  service: { ready_replicas: number; desired_replicas: number },
  downTone: 'red' | 'amber' = 'red',
): 'active' | 'degraded' | 'failed' {
  if (service.ready_replicas > 0 && service.ready_replicas >= service.desired_replicas) {
    return 'active';
  }
  if (service.ready_replicas === 0) return downTone === 'red' ? 'failed' : 'degraded';
  return 'degraded';
}

const SEVERITY_STROKE = {
  ok: 'hsl(var(--status-green))',
  warn: 'hsl(var(--status-amber))',
  crit: 'hsl(var(--status-red))',
} as const;

/** A single ratio against its limit, as a small ring whose colour carries severity. */
function LimitRing({ percent }: { readonly percent: number | null }): React.ReactElement {
  const r = 7;
  const length = 2 * Math.PI * r;
  const fill = percent === null ? 0 : Math.min(100, percent) / 100;
  const severity =
    percent === null
      ? null
      : percent >= APP_CAPACITY_CRITICAL_UTILIZATION
        ? 'crit'
        : percent >= APP_CAPACITY_WARN_UTILIZATION
          ? 'warn'
          : 'ok';
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true" className="shrink-0">
      <circle
        cx="9"
        cy="9"
        r={r}
        fill="none"
        stroke="hsl(var(--border))"
        strokeWidth="2.5"
        strokeDasharray={percent === null ? '2 2' : undefined}
      />
      {severity ? (
        <circle
          cx="9"
          cy="9"
          r={r}
          fill="none"
          stroke={SEVERITY_STROKE[severity]}
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeDasharray={`${fill * length} ${length}`}
          transform="rotate(-90 9 9)"
        />
      ) : null}
    </svg>
  );
}

function Usage({
  label,
  used,
  limit,
  format,
}: {
  readonly label: string;
  readonly used: number | null;
  readonly limit: number | null;
  readonly format: (value: number) => string;
}): React.ReactElement {
  const unlimited = limit === null || limit <= 0;
  const measured = used !== null && !unlimited;
  // Unrounded, so a replica at 94.6% is a warning, as the verdict judges it, not a critical ring.
  const percent = measured ? (used / limit) * 100 : null;
  return (
    <div className="flex min-w-0 items-center gap-2">
      {unlimited ? (
        <span className="size-[18px] shrink-0" aria-hidden="true" />
      ) : (
        <LimitRing percent={percent} />
      )}
      <p className="min-w-0 text-xs">
        <span className="text-muted-foreground">{label} </span>
        {measured ? (
          <span className="tabular-nums text-foreground">
            {format(used)} <span className="text-muted-foreground">of {format(limit)}</span>
          </span>
        ) : unlimited ? (
          <span className="text-muted-foreground">
            {used !== null ? (
              <>
                <span className="tabular-nums text-foreground">{format(used)}</span>,{' '}
              </>
            ) : null}
            no limit set
          </span>
        ) : (
          <span className="text-muted-foreground">not measured</span>
        )}
      </p>
    </div>
  );
}

/**
 * Millicores, the unit `kubectl top` and a pod spec use. A replica idling at one
 * millicore must not round to "0", which reads as a stopped process.
 */
const millicores = (cores: number): string => {
  const m = cores * 1000;
  return m > 0 && m < 1 ? '<1m' : `${Math.round(m).toLocaleString('en-US')}m`;
};
const mib = (bytes: number): string => `${Math.round(bytes / MIB).toLocaleString('en-US')} MiB`;

function ReplicaRow({
  replica,
  color,
}: {
  readonly replica: PlatformServiceReplica;
  /** Keys this replica's line on the History charts below. */
  readonly color: string | undefined;
}): React.ReactElement {
  const last = replica.last_termination;
  return (
    <li
      className="grid grid-cols-1 gap-x-4 gap-y-1 py-2 sm:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)_minmax(0,1.2fr)_auto] sm:items-center"
      data-testid={`replica-${replica.replica_id}`}
    >
      <p
        className="flex min-w-0 items-center gap-1.5 font-mono text-[11px] text-muted-foreground"
        title={replica.replica_id}
      >
        <SeriesDot color={color} />
        <span className="truncate">{replica.replica_id}</span>
      </p>
      <Usage
        label="CPU"
        used={replica.cpu.usage_cores}
        limit={replica.cpu.limit_cores}
        format={millicores}
      />
      <Usage
        label="Memory"
        used={replica.memory.usage_bytes}
        limit={replica.memory.limit_bytes}
        format={mib}
      />
      <p className="text-xs text-muted-foreground sm:text-right">
        {replica.restart_count === 0
          ? 'no restarts'
          : `${replica.restart_count} restart${replica.restart_count === 1 ? '' : 's'}`}
        {last?.reason ? (
          <span className="block text-[11px]">
            last: {humanCrashReason(last.reason, last.exit_code)}
            {last.finished_at ? ` · ${relativeTime(last.finished_at)}` : ''}
          </span>
        ) : null}
      </p>
    </li>
  );
}

function ServiceBlock({
  label,
  detail,
  status,
  replicas,
  history,
}: {
  readonly label: string;
  readonly detail: string;
  readonly status: React.ReactElement;
  readonly replicas: readonly PlatformServiceReplica[];
  readonly history?: (replicaIds: readonly string[]) => React.ReactNode;
}): React.ReactElement {
  const sorted = [...replicas].sort((a, b) => a.replica_id.localeCompare(b.replica_id));
  const ids = sorted.map((r) => r.replica_id);
  const colors = history ? seriesColors(ids) : new Map<string, string>();
  return (
    <section className="py-3 first:pt-0 last:pb-0">
      <div className="flex items-center justify-between gap-3 text-sm">
        <LabelWithHelp label={label} detail={detail} />
        {status}
      </div>
      {sorted.length > 0 ? (
        <ul className="mt-1 divide-y divide-border/60 pl-0 sm:pl-3">
          {sorted.map((replica) => (
            <ReplicaRow
              key={replica.replica_id}
              replica={replica}
              color={colors.get(replica.replica_id)}
            />
          ))}
        </ul>
      ) : null}
      {history?.(ids)}
    </section>
  );
}

/**
 * Every service Greenlight runs in the cluster: one block per service, and under
 * it every replica with what it is using against the limits it runs under. A
 * replica that reaches its memory limit is restarted by the cluster whatever
 * room its node has, which is why these are per replica rather than per node.
 */
export function ControlPlaneServicesSection({
  pod,
  services,
  historyFor,
  showUnreported = false,
}: {
  readonly pod: HealthSnapshot['pods']['control_plane'];
  readonly services?: readonly PlatformService[] | null;
  /**
   * Lists a platform service the snapshot did not carry as "not reported"
   * instead of leaving it out, for a reader whose source never carries them.
   */
  readonly showUnreported?: boolean;
  /**
   * A service's own history, collapsed under its replicas. `replicas` are the ids
   * the block lists, whose dots key the chart lines; `label` names the panel
   * where no block does.
   */
  readonly historyFor?: (
    service: HealthSeriesServiceKey,
    options: { readonly replicas?: readonly string[]; readonly label?: string },
  ) => React.ReactNode;
}): React.ReactElement {
  const tiers = !pod.read_error ? pod.tiers : undefined;
  const platform = PLATFORM_SERVICES.flatMap((meta) => {
    const service = services?.find((s) => s.key === meta.key);
    return service || showUnreported ? [{ meta, service }] : [];
  });
  return (
    <SectionCard
      title="Greenlight control plane services"
      testId="control-plane-services"
      className="md:col-span-2"
      badge={
        <span className="text-xs text-muted-foreground">
          {pod.restart_count_24h === undefined
            ? 'restarts in 24h not reported'
            : `${pod.restart_count_24h} restart${pod.restart_count_24h === 1 ? '' : 's'} in 24h across all services`}
        </span>
      }
    >
      <p className="mb-3 text-xs leading-relaxed text-muted-foreground">
        Greenlight&rsquo;s own services, each running as one or more <em>replicas</em> (copies of
        the same pod).
      </p>
      <TooltipProvider>
        <div className="divide-y divide-border">
          {tiers ? (
            CONTROL_PLANE_TIERS.map((meta) => {
              const tier = tiers[meta.key];
              return (
                <ServiceBlock
                  key={meta.key}
                  label={meta.label}
                  detail={meta.detail}
                  replicas={(pod.replicas ?? []).filter((r) => r.tier === meta.key)}
                  {...(historyFor
                    ? { history: (replicas) => historyFor(meta.key, { replicas }) }
                    : {})}
                  status={
                    <StatusBadge variant={readyVariant(tier)}>
                      {tier.ready_replicas}/{tier.desired_replicas} replicas ready
                    </StatusBadge>
                  }
                />
              );
            })
          ) : (
            <div className="flex items-center justify-between py-2 text-sm">
              <span className="text-muted-foreground">All services</span>
              <StatusBadge
                variant={
                  pod.read_error
                    ? 'failed'
                    : readyVariant({
                        ready_replicas: pod.ready_replicas,
                        desired_replicas: pod.desired_replicas,
                      })
                }
              >
                {pod.read_error
                  ? 'unreadable'
                  : `${pod.ready_replicas}/${pod.desired_replicas} replicas ready`}
              </StatusBadge>
            </div>
          )}
          {!tiers && historyFor ? (
            // The current read failed, which is when the recorded history matters most.
            <div className="space-y-2 py-2">
              {CONTROL_PLANE_TIERS.map((meta) => (
                <React.Fragment key={meta.key}>
                  {historyFor(meta.key, { label: `${meta.label} history` })}
                </React.Fragment>
              ))}
            </div>
          ) : null}
          {platform.map(({ meta, service }) => (
            <ServiceBlock
              key={meta.key}
              label={meta.label}
              detail={meta.detail}
              replicas={service?.replicas ?? []}
              {...(historyFor ? { history: (replicas) => historyFor(meta.key, { replicas }) } : {})}
              status={
                service === undefined ? (
                  <StatusBadge variant="inactive">not reported</StatusBadge>
                ) : service.read_error ? (
                  <StatusBadge variant="degraded">unreadable</StatusBadge>
                ) : (
                  <StatusBadge variant={readyVariant(service, meta.downTone)}>
                    {service.ready_replicas}/{service.desired_replicas} replicas ready
                  </StatusBadge>
                )
              }
            />
          ))}
        </div>
      </TooltipProvider>
    </SectionCard>
  );
}
